import React, { useCallback, useEffect, useState } from 'react';
import { MdAdd, MdDelete, MdEdit, MdSecurity } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { ProtectedComponent } from '../../../domain/components/rbac/ProtectedComponent';
import { Role } from '../../../domain/types/rbac';
import { RoleService } from '../../../domain/use-cases/rbac/role-service';
import Button from '../../components/button';
import Spinner from '../../components/spinner';

export const RolesPage: React.FC = () => {
  const navigate = useNavigate();
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const loadRoles = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const roleService = new RoleService();
      const rolesData = await roleService.getAllRoles();
      setRoles(rolesData);
    } catch (err) {
      console.error('Error loading roles:', err);
      setError('Error al cargar los roles');
    } finally {
      setLoading(false);
    }
  }, []);
  const handleDeleteRole = async (id: string) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este rol?')) {
      return;
    }

    try {
      setDeletingId(id);
      const roleService = new RoleService();
      await roleService.deleteRole(id);
      await loadRoles(); // Recargar la lista
    } catch (err) {
      setError('Error al eliminar el rol');
      console.error('Error deleting role:', err);
    } finally {
      setDeletingId(null);
    }
  };

  const navigateToAdd = () => {
    navigate('/dashboard/roles/add');
  };

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <MdSecurity className="w-8 h-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Gestión de Roles
            </h1>
            <p className="text-gray-600">
              Administra los roles y permisos del sistema
            </p>
          </div>
        </div>

        <ProtectedComponent resource="roles" action="create">
          <Button
            label="Nuevo Rol"
            onClick={navigateToAdd}
            icon={<MdAdd className="w-5 h-5" />}
          />
        </ProtectedComponent>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          <strong className="font-bold">Error:</strong>
          <span className="block sm:inline"> {error}</span>
        </div>
      )}

      {/* Roles Table */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            Roles del Sistema
          </h3>

          {roles.length === 0 ? (
            <div className="text-center py-8">
              <MdSecurity className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                No hay roles
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Comienza creando un nuevo rol para el sistema.
              </p>
              <ProtectedComponent resource="roles" action="create">
                <div className="mt-6">
                  <Button
                    label="Nuevo Rol"
                    onClick={navigateToAdd}
                    icon={<MdAdd className="w-5 h-5" />}
                  />
                </div>
              </ProtectedComponent>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Nombre del Rol
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Descripción
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Permisos
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {roles.map((role) => (
                    <tr key={role.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {role.name}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {role.description}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {role.permissions.length} permisos
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            role.isActive
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {role.isActive ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <ProtectedComponent resource="roles" action="update">
                            <button
                              onClick={() =>
                                navigate(`/dashboard/roles/${role.id}`)
                              }
                              className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-50"
                              title="Editar rol"
                            >
                              <MdEdit className="w-4 h-4" />
                            </button>
                          </ProtectedComponent>

                          <ProtectedComponent resource="roles" action="delete">
                            <button
                              onClick={() =>
                                role.id && handleDeleteRole(role.id)
                              }
                              disabled={deletingId === role.id}
                              className="text-red-600 hover:text-red-800 p-1 rounded disabled:opacity-50"
                              title="Eliminar rol"
                            >
                              {deletingId === role.id ? (
                                <Spinner />
                              ) : (
                                <MdDelete className="w-4 h-4" />
                              )}
                            </button>
                          </ProtectedComponent>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <MdSecurity className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Total de Roles
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {roles.length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="h-6 w-6 bg-green-500 rounded-full flex items-center justify-center">
                  <div className="h-2 w-2 bg-white rounded-full"></div>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Roles Activos
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {roles.filter((role) => role.isActive).length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="h-6 w-6 bg-red-500 rounded-full flex items-center justify-center">
                  <div className="h-2 w-2 bg-white rounded-full"></div>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Roles Inactivos
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {roles.filter((role) => !role.isActive).length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
